import { useMemo } from "react";
import { useShallow } from "zustand/react/shallow";
import { useCartStore } from "@/components/cart/store";
import type { CartItem } from "@/components/cart/types";
import type { Product } from "@/lib/products";
import {
  buildCartLines,
  getCartSubtotal,
  getQuantityByProductId,
} from "@/lib/storefront/cart-lines";
import { useStorefrontCatalogQuery } from "./use-storefront-catalog";

/**
 * The cart as the storefront shows it: each line with its product, price and
 * stock ceiling, the subtotal and item count, and quantity changes that never
 * go past the stock.
 */
export function useStorefrontCart() {
  const { cart, updateCartItem, removeFromCart } = useCartStore(
    useShallow((state) => ({
      cart: state.cart,
      updateCartItem: state.updateCartItem,
      removeFromCart: state.removeFromCart,
    }))
  );
  const { data: categories, isPending } = useStorefrontCatalogQuery();

  const productsById = useMemo(
    () =>
      new Map<string, Product>(
        (categories ?? [])
          .flatMap((category) => category.products)
          .map((product) => [product.id, product])
      ),
    [categories]
  );

  const lines = useMemo(
    () => buildCartLines(cart.items, productsById, cart.orderType),
    [cart.items, productsById, cart.orderType]
  );

  const quantityByProductId = useMemo(
    () => getQuantityByProductId(cart.items),
    [cart.items]
  );

  const setQuantity = (item: CartItem, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(item);
      return;
    }
    const line = lines.find((entry) => entry.item.id === item.id);
    const capped =
      line?.maxQuantity !== undefined
        ? Math.min(quantity, line.maxQuantity)
        : quantity;
    if (capped <= 0) {
      removeFromCart(item);
      return;
    }
    updateCartItem({ ...item, quantity: capped });
  };

  return {
    lines,
    // Lines whose product has left the webshop cannot be ordered.
    orderableLines: lines.filter((line) => !!line.product),
    isCatalogLoading: isPending,
    itemCount: cart.items.reduce((sum, item) => sum + item.quantity, 0),
    subtotal: getCartSubtotal(lines.filter((line) => !!line.product)),
    quantityByProductId,
    productsById,
    setQuantity,
    removeLine: (item: CartItem) => removeFromCart(item),
  };
}
